/**
 * site-data.js
 * 依据 docs/demo_web 数据模型整理的本地模拟数据层。
 * 结构对齐 demo_web：categories / products / featured / installations / colors / articles / warranty / dealers。
 * 后端 API 接入后，可逐步替换为接口数据。
 */

/* 五大产品族（对齐 demo_web categoryLabels） */
export const categories = [
  {
    id: 'paint-protection-film',
    slug: 'paint-protection-film',
    name: '漆面保护',
    nameEn: 'Paint Protection',
    image: '/images/home-family-paint-protection.webp',
    summary: '透明聚氨酯保护膜，抵御飞石、划痕与化学侵蚀，为原厂漆面提供长期守护。'
  },
  {
    id: 'color-wrap-film',
    slug: 'color-wrap-film',
    name: '车身改色',
    nameEn: 'Color Wraps',
    image: '/images/home-family-color-wrap.webp',
    summary: '乙烯基与 TPU 改色膜，覆盖多种颜色与表面效果，让车身表达独到之选。'
  },
  {
    id: 'window-film',
    slug: 'window-film',
    name: '玻璃隔热',
    nameEn: 'Window Tint',
    image: '/images/home-family-window-tint.webp',
    summary: '汽车玻璃隔热膜，兼顾清晰视野、热量控制与隐私保护。'
  },
  {
    id: 'special-protection',
    slug: 'special-protection',
    name: '专项防护',
    nameEn: 'Specialty Protection',
    image: '/images/home-family-specialty-protection.webp',
    summary: '大灯、天窗、门槛等部位的针对性保护，覆盖易损与易磨区域。'
  },
  {
    id: 'digital-print-film',
    slug: 'digital-print-film',
    name: '车身个性定制',
    nameEn: 'Custom Vehicle Graphics',
    image: '/images/home-family-custom-graphics.webp',
    summary: '可印刷薄膜与裁切图形，实现品牌涂装与个性化视觉方案。'
  }
]

/* 产品（含型号与规格） */
export const products = [
  {
    id: 'gloss-ppf',
    slug: 'gloss-ppf',
    categorySlug: 'paint-protection-film',
    name: 'LUX 系列 漆面保护膜',
    productName: 'LUX SERIES PPF',
    descriptor: '高透亮隐形车衣，自动修复细微划痕，抵御飞石与化学侵蚀。',
    effect: '亮面高透效果，装贴后几乎不可见，为原厂漆面提供长期保护。',
    image: '/images/concept-hero.webp',
    series: [
      { name: 'LUX-LS', models: [
        { code: 'LUX-LSG100', description: '旗舰亮面型号 · 标称总厚度 8.2 mil', nominalTotalThickness: '8.2 mil' },
        { code: 'LUX-LSG80', description: '亮面标准型号 · 标称总厚度 8.0 mil', nominalTotalThickness: '8.0 mil' }
      ] }
    ],
    advantages: ['高透亮光学涂层', '自动修复细微划痕', '抗紫外线不黄变', '可移除不残胶'],
    applications: ['前保险杠与引擎盖', '翼子板与门板', '门碗与门槛', '后视镜与 A 柱'],
    specs: [
      { label: '标称总厚度', value: '8.2 mil' },
      { label: '成膜保留厚度', value: '6.8 mil' },
      { label: '光固化单涂层厚度', value: '0.8 mil' }
    ],
    installation: '建议由经过培训的专业安装技师施工，使用专用安装液与刮板，干贴/湿贴均可。',
    care: '装贴后 24 小时内避免洗车与高速行驶，日常使用中性洗车液清洁即可。',
    warranty: '享受官方有限产品质保，覆盖脱层、黄变与起泡等制造缺陷。'
  },
  {
    id: 'gloss-black-ppf',
    slug: 'gloss-black-ppf',
    categorySlug: 'paint-protection-film',
    name: 'PIANO BLACK 钢琴黑保护膜',
    productName: 'PIANO BLACK PPF',
    descriptor: '高光钢琴黑 TPU 保护膜，兼顾改色与保护。',
    effect: '深邃亮黑效果，同时具备 TPU 保护膜的抗划与自修复能力。',
    image: '/images/concept-piano-black.webp',
    models: ['PIANO BLACK'],
    advantages: ['高光钢琴黑质感', 'TPU 自修复涂层', '可替代改色膜'],
    applications: ['全车改色', '车顶与立柱', '细节装饰'],
    specs: [
      { label: '标称总厚度', value: '8.0 mil' },
      { label: '表面效果', value: '亮面 / Gloss' }
    ],
    installation: '建议由专业技师施工，注意包边与曲面处理。',
    care: '与亮面漆面保护膜养护方式一致。',
    warranty: '享受官方有限产品质保。'
  },
  {
    id: 'matte-ppf',
    slug: 'matte-ppf',
    categorySlug: 'paint-protection-film',
    name: 'MATTE 系列 哑光保护膜',
    productName: 'MATTE SERIES PPF',
    descriptor: '哑光质感透明保护膜，低调质感与全面防护兼得。',
    effect: '哑光低反光效果，隐藏细微划痕，呈现沉稳质感。',
    image: '/images/concept-wrap-detail.webp',
    series: [
      { name: 'MATTE-LS', models: [
        { code: 'MATTE-LSM100', description: '旗舰哑光型号 · 标称总厚度 8.2 mil', nominalTotalThickness: '8.2 mil' }
      ] }
    ],
    advantages: ['低反光哑光质感', '抗划与自修复', '不改变原厂哑光/缎面漆面'],
    applications: ['全车哑光保护', '局部哑光点缀'],
    specs: [{ label: '标称总厚度', value: '8.2 mil' }],
    installation: '建议由专业技师施工，避免划痕残留。',
    care: '使用中性清洁剂，避免研磨性产品。',
    warranty: '享受官方有限产品质保。'
  },
  {
    id: 'vinyl-color-wrap',
    slug: 'vinyl-color-wrap',
    categorySlug: 'color-wrap-film',
    name: '乙烯基改色膜',
    productName: 'VINYL COLOR WRAP',
    descriptor: '丰富色系与表面效果的乙烯基改色膜，灵活表达个性。',
    effect: '覆盖原厂漆面，提供多种颜色、光泽与纹理选择。',
    image: '/images/concept-wrap-detail.webp',
    advantages: ['丰富颜色选择', '施工灵活', '可移除'],
    applications: ['全车改色', '局部点缀', '商业涂装'],
    specs: [{ label: '材料', value: '乙烯基 / Vinyl' }],
    installation: '湿贴施工，适合曲面与复杂造型。',
    care: '避免高压水枪直冲包边位置。',
    warranty: '享受官方有限产品质保。'
  },
  {
    id: 'tpu-color-wrap',
    slug: 'tpu-color-wrap',
    categorySlug: 'color-wrap-film',
    name: 'TPU 改色膜',
    productName: 'TPU COLOR WRAP',
    descriptor: '兼具改色与保护的 TPU 改色膜，自修复涂层更耐用。',
    effect: '彩色 TPU 涂层 + 自修复表面，颜色与保护兼得。',
    image: '/images/concept-wrap-detail.webp',
    advantages: ['TPU 自修复', '色彩持久', '抗石击'],
    applications: ['全车改色', '高性能保护'],
    specs: [{ label: '材料', value: 'TPU' }],
    installation: '建议由专业技师施工。',
    care: '与 PPF 养护一致。',
    warranty: '享受官方有限产品质保。'
  },
  {
    id: 'automotive-window-film',
    slug: 'automotive-window-film',
    categorySlug: 'window-film',
    name: '汽车隔热膜',
    productName: 'AUTOMOTIVE WINDOW FILM',
    descriptor: '兼顾清晰视野与热量控制的汽车玻璃隔热膜。',
    effect: '显著降低车内温度与紫外线辐射，保持清晰视野。',
    image: '/images/concept-window-clarity.webp',
    variants: [
      { code: 'WIN-CL30', vlt: 30 },
      { code: 'WIN-CL50', vlt: 50 },
      { code: 'WIN-CL70', vlt: 70 }
    ],
    advantages: ['高隔热率', '低内反光', '抗紫外线'],
    applications: ['前挡玻璃', '侧窗', '后挡玻璃'],
    specs: [
      { label: '可见光透过率范围', value: '30% – 70% VLT' },
      { label: '紫外线阻隔率', value: '≥ 99%' }
    ],
    installation: '建议由专业技师在无尘环境施工。',
    care: '装贴后 7 天内避免升降车窗。',
    warranty: '享受官方有限产品质保。'
  },
  {
    id: 'headlight-protection',
    slug: 'headlight-protection',
    categorySlug: 'special-protection',
    name: '大灯保护膜',
    productName: 'HEADLIGHT PROTECTION',
    descriptor: '针对大灯玻璃的耐冲击保护膜，抵御飞石与泛黄。',
    effect: '高透光率的同时抵御飞石冲击，延缓大灯老化泛黄。',
    image: '/images/concept-headlight.webp',
    advantages: ['高透光', '抗冲击', '抗UV泛黄'],
    applications: ['大灯', '雾灯'],
    specs: [{ label: '标称总厚度', value: '7.5 mil' }],
    installation: '建议由专业技师施工，注意曲面贴合。',
    care: '与漆面保护膜养护一致。',
    warranty: '享受官方有限产品质保。'
  },
  {
    id: 'sunroof-film',
    slug: 'sunroof-film',
    categorySlug: 'special-protection',
    name: '天窗膜',
    productName: 'SUNROOF FILM',
    descriptor: '降低全景天窗热量与紫外线的专用隔热膜。',
    effect: '显著降低天窗区域热量积聚。',
    image: '/images/concept-window-clarity.webp',
    advantages: ['隔热降温', '抗紫外线', '保持视野'],
    applications: ['全景天窗', '普通天窗'],
    specs: [{ label: '可见光透过率', value: '15% VLT' }],
    installation: '建议由专业技师施工。',
    care: '避免尖锐物体接触。',
    warranty: '享受官方有限产品质保。'
  },
  {
    id: 'custom-vehicle-graphics',
    slug: 'custom-vehicle-graphics',
    categorySlug: 'digital-print-film',
    name: '车身个性定制膜',
    productName: 'CUSTOM VEHICLE GRAPHICS',
    descriptor: '可印刷薄膜与裁切图形，实现品牌涂装与个性视觉。',
    effect: '支持数码印刷与裁切，呈现完整品牌或个性图案。',
    image: '/images/concept-custom-graphics.webp',
    advantages: ['支持数码印刷', '颜色还原度高', '可移除'],
    applications: ['品牌涂装', '赛事拉花', '个性图形'],
    specs: [{ label: '材料', value: '可印刷乙烯基' }],
    installation: '建议由专业贴膜技师施工。',
    care: '避免研磨性清洁。',
    warranty: '享受官方有限产品质保。'
  }
]

/* 精选产品（首页 Featured） */
export const featuredProducts = ['gloss-ppf', 'gloss-black-ppf', 'automotive-window-film']

/* 实车案例（Gallery） */
export const installations = [
  {
    id: 'porsche-911-gloss',
    title: '保时捷 911 · 全车 LUX 亮面保护',
    vehicleMake: 'Porsche', vehicleModel: '911 Carrera', vehicleYear: '2023',
    colorId: 'gloss-clear',
    productIds: ['gloss-ppf'],
    image: '/images/concept-hero.webp',
    caption: '全车亮面透明保护，保持原厂漆面质感。',
    area: '全车漆面'
  },
  {
    id: 'tesla-model3-piano',
    title: '特斯拉 Model 3 · 钢琴黑改色保护',
    vehicleMake: 'Tesla', vehicleModel: 'Model 3', vehicleYear: '2022',
    colorId: 'piano-black',
    productIds: ['gloss-black-ppf'],
    image: '/images/concept-piano-black.webp',
    caption: '钢琴黑 TPU 改色保护一体方案。',
    area: '全车改色'
  },
  {
    id: 'bmw-m4-matte',
    title: 'BMW M4 · 哑光透明保护',
    vehicleMake: 'BMW', vehicleModel: 'M4 Competition', vehicleYear: '2023',
    colorId: 'gloss-clear',
    productIds: ['matte-ppf'],
    image: '/images/concept-wrap-detail.webp',
    caption: '哑光透明保护膜，低反光质感。',
    area: '全车漆面'
  },
  {
    id: 'mercedes-e300-window',
    title: '奔驰 E300 · 全车隔热膜',
    vehicleMake: 'Mercedes-Benz', vehicleModel: 'E 300 L', vehicleYear: '2021',
    colorId: 'window-clear',
    productIds: ['automotive-window-film'],
    image: '/images/concept-window-clarity.webp',
    caption: '前挡 70% VLT，侧后挡 30% VLT 组合。',
    area: '全车玻璃'
  },
  {
    id: 'audi-a6-headlight',
    title: '奥迪 A6 · 大灯保护',
    vehicleMake: 'Audi', vehicleModel: 'A6 L', vehicleYear: '2022',
    colorId: 'gloss-clear',
    productIds: ['headlight-protection'],
    image: '/images/concept-headlight.webp',
    caption: '大灯玻璃耐冲击保护。',
    area: '大灯'
  },
  {
    id: 'toyota-supra-custom',
    title: '丰田 Supra · 品牌涂装定制',
    vehicleMake: 'Toyota', vehicleModel: 'GR Supra', vehicleYear: '2020',
    colorId: 'custom-red',
    productIds: ['custom-vehicle-graphics'],
    image: '/images/concept-custom-graphics.webp',
    caption: '可印刷薄膜实现赛事品牌涂装。',
    area: '全车涂装'
  }
]

/* 颜色库（Colors） */
export const colors = [
  { id: 'gloss-clear', slug: 'gloss-clear', code: 'CL-001', name: '亮面透明', collection: 'TPU 保护', family: '透明', image: '/images/concept-hero.webp', description: '高透亮隐形车衣效果。' },
  { id: 'piano-black', slug: 'piano-black', code: 'PB-001', name: '钢琴黑', collection: 'TPU 改色', family: '黑色', image: '/images/concept-piano-black.webp', description: '深邃高光钢琴黑。' },
  { id: 'satin-black', slug: 'satin-black', code: 'SB-001', name: '缎面黑', collection: '乙烯基改色', family: '黑色', image: '/images/concept-wrap-detail.webp', description: '低反光缎面质感。' },
  { id: 'pearl-white', slug: 'pearl-white', code: 'PW-001', name: '珍珠白', collection: '乙烯基改色', family: '白色', image: '/images/concept-wrap-detail.webp', description: '温润珍珠光泽。' },
  { id: 'graphite-grey', slug: 'graphite-grey', code: 'GG-001', name: '石墨灰', collection: 'TPU 改色', family: '灰色', image: '/images/concept-wrap-detail.webp', description: '沉稳石墨金属质感。' },
  { id: 'window-clear', slug: 'window-clear', code: 'WC-001', name: '隔热透明', collection: '玻璃隔热', family: '透明', image: '/images/concept-window-clarity.webp', description: '清晰隔热透明膜。' },
  { id: 'racing-red', slug: 'racing-red', code: 'RR-001', name: '竞速红', collection: '乙烯基改色', family: '红色', image: '/images/concept-custom-graphics.webp', description: '高饱和竞速红。' },
  { id: 'custom-print', slug: 'custom-print', code: 'CP-001', name: '定制印刷', collection: '个性定制', family: '定制', image: '/images/concept-custom-graphics.webp', description: '支持任意图案印刷。' }
]

/* 洞察与新闻（Editorial） */
export const articles = [
  {
    id: 'ppf-vs-wrap-difference',
    type: 'insight',
    category: '选购指南',
    title: '漆面保护膜与改色膜，到底怎么选？',
    excerpt: '从材料、效果与使用场景出发，理清 PPF 与改色膜的核心区别，帮助你做出合适选择。',
    date: '2026-08-18',
    readTime: '8 分钟',
    relatedProductSlugs: ['gloss-ppf', 'tpu-color-wrap'],
    body: [
      { heading: '两者定位不同', text: '漆面保护膜（PPF）的核心是保护原厂漆面，改色膜的核心是改变车身外观。前者追求"看不见"，后者追求"看得见"。' },
      { heading: '材料与寿命', text: 'PPF 通常使用 TPU 基材并带自修复涂层，寿命可达 5-8 年；传统改色膜以乙烯基为主，寿命约 2-5 年。TPU 改色膜则兼顾两者。' },
      { heading: '如何选择', text: '若目标是保值与防刮，优先考虑 PPF；若目标是表达个性，可以选择改色膜或 TPU 改色膜。也可以组合使用：车身主体贴 PPF，细节部位用改色膜点缀。' }
    ]
  },
  {
    id: 'window-film-vlt-guide',
    type: 'insight',
    category: '技术解析',
    title: '汽车隔热膜 VLT 怎么选？一图看懂',
    excerpt: 'VLT（可见光透过率）是隔热膜最核心的参数。不同法规与使用场景下，前挡、侧窗该如何搭配？',
    date: '2026-07-30',
    readTime: '6 分钟',
    relatedProductSlugs: ['automotive-window-film'],
    body: [
      { heading: '什么是 VLT', text: 'VLT 指可见光透过玻璃膜的百分比。数值越高视野越通透，数值越低隐私性越强。' },
      { heading: '前挡建议', text: '前挡对视线要求最高，建议选择 70% VLT 以上的高透型号，兼顾隔热与安全。' },
      { heading: '侧后挡搭配', text: '侧后挡可根据隐私与隔热需求选择 15%-50% VLT，但需符合当地法规要求。' }
    ]
  },
  {
    id: 'new-series-lux-ls',
    type: 'news',
    category: '品牌动态',
    title: '全新 LUX-LS 旗舰系列正式发布',
    excerpt: 'LUX-LSG100 与 LUX-LSG80 两款亮面型号上市，以更高透亮与更稳定的施工表现为核心卖点。',
    date: '2026-08-02',
    readTime: '4 分钟',
    relatedProductSlugs: ['gloss-ppf'],
    body: [
      { heading: '产品发布', text: 'LUX-LS 系列是面向高端市场的旗舰 PPF 产品线，提供 8.2 mil 与 8.0 mil 两个厚度选择。' },
      { heading: '核心升级', text: '升级后的光固化涂层透亮度和抗黄变能力均有提升，同时优化了施工宽容度。' }
    ]
  },
  {
    id: 'dealer-program-update',
    type: 'news',
    category: '渠道动态',
    title: '2026 授权门店合作计划开放申请',
    excerpt: '面向全国开放授权安装门店与区域合作伙伴申请，提供培训、物料与质保系统支持。',
    date: '2026-06-15',
    readTime: '3 分钟',
    relatedProductSlugs: ['gloss-ppf'],
    body: [
      { heading: '合作计划', text: '授权门店可获得官方培训认证、样片物料与统一的质保查询系统支持。' },
      { heading: '申请方式', text: '通过官网联系表单提交门店信息，团队将在 3 个工作日内与您联系。' }
    ]
  }
]

/* 质保服务政策段落 */
export const warrantySections = [
  { title: '质保覆盖范围', body: '官方有限产品质保覆盖制造缺陷导致的脱层、起泡、黄变与过度收缩等问题，具体以正式条款为准。' },
  { title: '登记与申请方式', body: '请准备产品名称、购买与安装凭证、安装日期和门店信息，通过官网质保查询登记，或按正式条款提交申请。' },
  { title: '注意事项', body: '质保不覆盖因事故、人为损伤、不当施工或养护造成的损坏。请保留安装凭证与产品批次信息。' }
]

/* 授权门店（Dealers 模拟数据） */
export const dealers = [
  { id: 1, name: '深圳旗舰体验中心', city: '深圳', address: '南山区科技园路 88 号', phone: '0755-8888-0001', authorized: true },
  { id: 2, name: '上海陆家嘴授权店', city: '上海', address: '浦东新区世纪大道 1200 号', phone: '021-6666-0002', authorized: true },
  { id: 3, name: '北京朝阳授权店', city: '北京', address: '朝阳区望京街 10 号', phone: '010-5555-0003', authorized: true },
  { id: 4, name: '广州天河授权店', city: '广州', address: '天河区珠江新城华夏路 30 号', phone: '020-7777-0004', authorized: true },
  { id: 5, name: '成都高新授权店', city: '成都', address: '高新区天府大道 999 号', phone: '028-3333-0005', authorized: false }
]

/* 质保查询模拟结果 */
export const mockWarrantyRecord = {
  reference: 'SN-WR-2026-0915-001',
  productName: 'LUX-LSG100 漆面保护膜',
  batchReference: 'LOT-2026-0712-A',
  storeName: '深圳旗舰体验中心',
  authorizationStatus: '授权门店',
  installationDate: '2026-08-20',
  coverageEndsAt: '2031-08-20',
  status: '有效',
  firstVerifiedAt: '2026-09-15 10:24:00'
}

export const mockAuthenticationRecord = {
  code: 'SN-AUTH-8842',
  productName: 'LUX-LSG100 漆面保护膜',
  batchReference: 'LOT-2026-0712-A',
  status: '正品验证通过 · 首次查询',
  firstVerifiedAt: '2026-09-15 10:24:00'
}

export const mockStoreRecord = {
  storeCode: 'SN-0001',
  storeName: '深圳旗舰体验中心',
  city: '深圳',
  authorizationStatus: '有效',
  authorizationExpiresAt: '2027-09-30',
  authorizedProducts: ['漆面保护膜', '车身改色膜', '玻璃隔热膜']
}

/* 品牌信息 */
export const brand = {
  name: 'SCOTT NOLES',
  tagline: 'Protect what matters. Express what is yours.',
  intro: '探索汽车保护、改色与隔热产品，为车身外观与驾乘体验找到合适之选。',
  brandBody: '我们聚焦汽车表面的保护、颜色与隔热应用。产品选择需结合材料、表面效果、施工条件与养护需求。',
  supportEmail: 'support@autocare.example.com',
  contactEmail: 'contact@autocare.example.com',
  companyName: 'Auto Care 示例品牌（迁移自 Scott Noles 页面结构）',
  telephone: '+86 400-000-0000'
}

export default {
  categories,
  products,
  featuredProducts,
  installations,
  colors,
  articles,
  warrantySections,
  dealers,
  mockWarrantyRecord,
  mockAuthenticationRecord,
  mockStoreRecord,
  brand
}
